import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '../environments/environment';
import { v4 as uuidv4 } from 'uuid';

import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { UserSelector } from '@tix/auth/state';

export interface LogEntry {
  message: string;
  event: string;
  data?: any;
}

@Injectable({ providedIn: 'root' })
export class LoggerService implements OnDestroy {
  private readonly AXIOM_API_URL = 'https://api.axiom.co/v1/datasets';
  private readonly MAX_QUEUE_SIZE = 1000;
  private readonly FLUSH_INTERVAL = 3000;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000;

  private requestId: string;
  private os: string;
  private logQueue: any[] = [];
  private flushInterval: any;
  private isFlushInProgress = false;
  private lastFlushAttempt = 0;
  private currentUserId: string | null = null;
  private userSub: Subscription;
  private host: string;

  constructor(private store: Store) {
    // Generate a new requestId for this session
    this.requestId = uuidv4();

    // Detect OS
    this.os = this.detectOS();

    // host
    this.host = this.detectHost();
    // Subscribe to auth user
    this.userSub = this.store
      .select(UserSelector.getAuthenticatedUser)
      .subscribe(user => {
        this.currentUserId = user?.uid ?? null;
      });

    // Set up auto-flush interval
    this.flushInterval = setInterval(() => {
      this.flush().catch(err => console.error('Periodic flush failed:', err));
    }, this.FLUSH_INTERVAL);

    // Flush on window unload
    if (typeof window !== 'undefined') {
      window.addEventListener('unload', () => {
        // Final flush using fetch
        fetch(`${this.AXIOM_API_URL}/${environment.axiom.dataset}/ingest`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${environment.axiom.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.logQueue),
          // Use keepalive to ensure the request completes
          keepalive: true
        }).catch(e => console.error('Failed to send final logs:', e));
      });
    }
  }

  private detectOS(): string {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) return 'windows';
    if (userAgent.includes('mac')) return 'macos';
    if (userAgent.includes('linux')) return 'linux';
    if (userAgent.includes('android')) return 'android';
    if (userAgent.includes('ios')) return 'ios';
    return 'unknown';
  }

  private detectHost(): string {
    const host = window.location.host;
    return host || 'unknown';
  }

  private formatLogData(
    level: string,
    message: string,
    eventName: string,
    userId: string | null,
    data?: any
  ) {
    return {
      os: this.os,
      requestId: this.requestId,
      host: this.host,
      _time: new Date().toISOString(),
      level,
      message,
      event: eventName,
      userId: userId || 'anonymous',
      source: 'asylumtix_web',
      ...data
    };
  }

  private async queueLog(
    level: 'info' | 'warn' | 'error' | 'debug',
    message: string,
    eventName: string,
    userId: string | null,
    data?: any
  ) {
    try {
      const logData = this.formatLogData(
        level,
        message,
        eventName,
        userId,
        data
      );

      // Console logging
      switch (level) {
        case 'info':
          console.info(message, logData);
          break;
        case 'warn':
          console.warn(message, logData);
          break;
        case 'error':
          console.error(message, logData);
          break;
        case 'debug':
          console.debug(message, logData);
          break;
      }

      // Queue management
      if (this.logQueue.length >= this.MAX_QUEUE_SIZE) {
        // Queue is full, force a flush
        await this.flush();
        if (this.logQueue.length >= this.MAX_QUEUE_SIZE) {
          // If still full after flush, remove oldest items
          console.warn(
            `Log queue overflow, dropping ${
              this.logQueue.length - this.MAX_QUEUE_SIZE + 1
            } old events`
          );
          this.logQueue.splice(
            0,
            this.logQueue.length - this.MAX_QUEUE_SIZE + 1
          );
        }
      }

      this.logQueue.push(logData);

      // Check if we should trigger a flush
      const now = Date.now();
      if (
        this.logQueue.length >= 100 ||
        now - this.lastFlushAttempt >= this.FLUSH_INTERVAL
      ) {
        this.flush().catch(err => console.error('Failed to flush logs:', err));
      }
    } catch (error) {
      console.error('Failed to queue log:', error);
    }
  }

  info(entryOrMessage: LogEntry | string, event?: string, data?: any) {
    if (typeof entryOrMessage === 'object' && entryOrMessage !== null) {
      const { message, event: evt, data: d } = entryOrMessage;
      this.queueLog('info', message, evt, this.currentUserId, d);
    } else {
      this.queueLog(
        'info',
        entryOrMessage as string,
        event as string,
        this.currentUserId,
        data
      );
    }
  }

  warn(entryOrMessage: LogEntry | string, event?: string, data?: any) {
    if (typeof entryOrMessage === 'object' && entryOrMessage !== null) {
      const { message, event: evt, data: d } = entryOrMessage;
      this.queueLog('warn', message, evt, this.currentUserId, d);
    } else {
      this.queueLog(
        'warn',
        entryOrMessage as string,
        event as string,
        this.currentUserId,
        data
      );
    }
  }

  error(entryOrMessage: LogEntry | string, event?: string, data?: any) {
    if (typeof entryOrMessage === 'object' && entryOrMessage !== null) {
      const { message, event: evt, data: d } = entryOrMessage;
      this.queueLog('error', message, evt, this.currentUserId, d);
    } else {
      this.queueLog(
        'error',
        entryOrMessage as string,
        event as string,
        this.currentUserId,
        data
      );
    }
  }

  debug(entryOrMessage: LogEntry | string, event?: string, data?: any) {
    if (typeof entryOrMessage === 'object' && entryOrMessage !== null) {
      const { message, event: evt, data: d } = entryOrMessage;
      this.queueLog('debug', message, evt, this.currentUserId, d);
    } else {
      this.queueLog(
        'debug',
        entryOrMessage as string,
        event as string,
        this.currentUserId,
        data
      );
    }
  }

  private async sendLogs(events: any[]): Promise<void> {
    try {
      const response = await fetch(
        `${this.AXIOM_API_URL}/${environment.axiom.dataset}/ingest`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${environment.axiom.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(events)
        }
      );

      if (!response.ok) {
        console.error(
          'Failed to send logs:',
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error('Failed to send logs:', error);
    }
  }

  async flush(): Promise<void> {
    if (this.logQueue.length === 0 || this.isFlushInProgress) return;

    try {
      this.isFlushInProgress = true;
      this.lastFlushAttempt = Date.now();

      // Take all events from the queue
      const eventsToSend = this.logQueue.splice(0, this.logQueue.length);

      // Send logs and don't wait for response
      this.sendLogs(eventsToSend);
    } catch (error) {
      console.error('Failed to flush logs:', error);
    } finally {
      this.isFlushInProgress = false;
    }
  }

  ngOnDestroy() {
    // Clean up user subscription
    this.userSub?.unsubscribe();
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flush().catch(console.error);
  }
}
